import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Camera, Video, Film, Package, Truck, CheckCircle2, AlertTriangle, Clock, User, Plus, Loader2, ChevronRight, Upload, Image as ImageIcon, X as XIcon, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatApiError } from "../lib/format";
import { PRODUCTION_STAGES, PBH_DIVISION_ID } from "../lib/constants";

const STAGE_ICONS = {
  "Shooting": Camera, "Backup": Package, "Selection": Film,
  "Photo Editing": Film, "Video Editing": Video,
  "Internal Review": User, "Client Preview": User, "Revision": AlertTriangle,
  "Final Approval": CheckCircle2, "Album Print": Package, "Packaging": Package,
  "Delivery": Truck, "Completed": CheckCircle2,
};

export default function Production() {
  const { user } = useAuth();
  const isManager = user && (["owner","admin","general_manager"].includes(user.role) ||
    (user.role === "division_manager" && user.division_id === PBH_DIVISION_ID));

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: jobs }, { data: st }] = await Promise.all([
        api.get("/production"), api.get("/production/stats"),
      ]);
      setRows(jobs || []); setStats(st);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat production jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (stageFilter !== "all" && r.stage !== stageFilter) return false;
      if (q) {
        const s = `${r.booking_code||""} ${r.client_name||""} ${r.event_name||""} ${r.photographer_name||""} ${r.videographer_name||""}`.toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, stageFilter, q]);

  return (
    <div className="space-y-6" data-testid="production-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Project by Historia</div>
          <h1 className="font-display text-2xl font-semibold">Production Pipeline</h1>
          <p className="text-xs text-[#5C6066] mt-1">Pantau progress shooting → editing → delivery untuk semua job PBH.</p>
        </div>
        {isManager && (
          <Button onClick={() => setCreateOpen(true)} className="bg-[#8B262D] text-white hover:bg-[#6E1C22]" data-testid="btn-new-production">
            <Plus className="w-4 h-4 mr-1.5"/> Job Produksi Baru
          </Button>
        )}
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {PRODUCTION_STAGES.map(s => {
            const Icon = STAGE_ICONS[s] || Film;
            const count = stats.by_stage?.[s] || 0;
            return (
              <button key={s} onClick={() => setStageFilter(s)}
                className={`p-3 rounded-lg border text-left transition-colors ${stageFilter===s?'bg-[#8B262D] text-white border-[#8B262D]':'bg-white border-[#E5E0D8] hover:border-[#8B262D]'}`}
                data-testid={`stage-tile-${s.replace(/\s+/g,'-').toLowerCase()}`}>
                <Icon className="w-4 h-4 mb-1"/>
                <div className={`text-[10px] uppercase tracking-wide font-semibold ${stageFilter===s?'text-white/80':'text-[#8C9096]'}`}>{s}</div>
                <div className="text-lg font-semibold font-mono">{count}</div>
              </button>
            );
          })}
          <button onClick={() => setStageFilter("all")}
            className={`p-3 rounded-lg border text-left transition-colors ${stageFilter==='all'?'bg-[#1E2022] text-white border-[#1E2022]':'bg-white border-[#E5E0D8] hover:border-[#1E2022]'}`}
            data-testid="stage-tile-all">
            <div className={`text-[10px] uppercase tracking-wide font-semibold ${stageFilter==='all'?'text-white/80':'text-[#8C9096]'}`}>Semua</div>
            <div className="text-lg font-semibold font-mono">{stats.total}</div>
          </button>
        </div>
      )}
      {stats?.overdue > 0 && (
        <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4"/> <b>{stats.overdue}</b> job production terlambat dari deadline
        </div>
      )}

      {/* Filter bar */}
      <Card className="p-4 border-[#E5E0D8] flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Cari</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Booking / klien / crew..."
            className="mt-1.5 h-10 bg-white" data-testid="production-search"/>
        </div>
        <div className="min-w-[180px]">
          <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Stage</Label>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="mt-1.5 h-10 bg-white" data-testid="production-stage-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Stage</SelectItem>
              {PRODUCTION_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden border-[#E5E0D8]">
        {loading ? (
          <div className="p-8 text-center text-[#8C9096]"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/> Memuat...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#8C9096] text-sm">Belum ada job production yang cocok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[10px] uppercase tracking-widest text-[#8C9096]">
                <tr>
                  <th className="text-left p-3">Booking / Klien</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Stage</th>
                  <th className="text-left p-3">Progress</th>
                  <th className="text-left p-3">Crew</th>
                  <th className="text-left p-3">Deadline</th>
                  <th className="text-right p-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(j => (
                  <tr key={j.id} className="border-t border-[#EFE9DE] hover:bg-[#FAF6EE]/50" data-testid={`production-row-${j.id}`}>
                    <td className="p-3">
                      <div className="font-mono text-[11px] text-[#8B262D]">{j.booking_code || "-"}</div>
                      <div className="font-medium">{j.client_name || "-"}</div>
                      <div className="text-[11px] text-[#8C9096]">{j.event_name}</div>
                    </td>
                    <td className="p-3 capitalize">{j.job_type}</td>
                    <td className="p-3">
                      <Badge className="bg-sky-50 text-sky-700 border-sky-200 font-mono text-[10px]">{j.stage}</Badge>
                    </td>
                    <td className="p-3">
                      <div className="w-24 h-1.5 bg-[#EFE9DE] rounded-full overflow-hidden">
                        <div className="h-full bg-[#8B262D]" style={{ width: `${j.progress_pct||0}%` }}/>
                      </div>
                      <div className="text-[10px] text-[#8C9096] mt-1 font-mono">{j.progress_pct||0}%</div>
                    </td>
                    <td className="p-3 text-xs text-[#5C6066]">
                      {j.photographer_name && <div>📸 {j.photographer_name}</div>}
                      {j.videographer_name && <div>🎥 {j.videographer_name}</div>}
                      {j.editors?.length > 0 && <div>✂️ {j.editors.map(e=>e.name).join(", ")}</div>}
                    </td>
                    <td className="p-3 text-xs">
                      {j.deadline ? (
                        <span className={j.is_overdue ? "text-rose-700 font-semibold" : ""}>
                          {formatDate(j.deadline)} {j.is_overdue && "⚠️"}
                        </span>
                      ) : <span className="text-[#8C9096]">—</span>}
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditJob(j)} data-testid={`production-edit-${j.id}`}>
                        Update <ChevronRight className="w-3.5 h-3.5 ml-1"/>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createOpen && <ProductionForm onClose={() => { setCreateOpen(false); load(); }}/>}
      {editJob && <ProductionUpdate job={editJob} isManager={isManager} onClose={() => { setEditJob(null); load(); }}/>}
    </div>
  );
}

function ProductionForm({ onClose }) {
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ booking_id: "", job_type: "photo", stage: "Shooting", progress_pct: 0, deadline: "", start_date: "", notes: "", photographer_id: "", videographer_id: "", editor_ids: [], assistant_ids: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/bookings").then(({data}) => setBookings((data||[]).filter(b => (b.divisions||[]).some(d => d.id === PBH_DIVISION_ID))));
    api.get("/employees").then(({data}) => setStaff((data||[]).filter(u => u.division_id === PBH_DIVISION_ID)));
  }, []);
  const submit = async () => {
    if (!form.booking_id) { toast.error("Pilih booking dulu"); return; }
    setBusy(true);
    try {
      await api.post("/production", form);
      toast.success("Production job dibuat");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg" data-testid="production-create-modal">
        <DialogHeader><DialogTitle>Job Produksi Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Booking PBH</Label>
            <Select value={form.booking_id} onValueChange={(v) => setForm({...form, booking_id: v})}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="prod-form-booking"><SelectValue placeholder="Pilih booking"/></SelectTrigger>
              <SelectContent>{bookings.map(b => <SelectItem key={b.id} value={b.id}>{b.booking_code} — {b.event_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Type</Label>
              <Select value={form.job_type} onValueChange={(v) => setForm({...form, job_type: v})}>
                <SelectTrigger className="mt-1.5 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="photo">Photo</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="album">Album</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Stage Awal</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}>
                <SelectTrigger className="mt-1.5 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>{PRODUCTION_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Photographer</Label>
            <Select value={form.photographer_id || "none"} onValueChange={(v) => setForm({...form, photographer_id: v==='none'?'':v})}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="prod-form-photographer"><SelectValue placeholder="Pilih"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tidak ada —</SelectItem>
                {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.position||s.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Videographer</Label>
            <Select value={form.videographer_id || "none"} onValueChange={(v) => setForm({...form, videographer_id: v==='none'?'':v})}>
              <SelectTrigger className="mt-1.5 bg-white"><SelectValue placeholder="Pilih"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tidak ada —</SelectItem>
                {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Start</Label>
              <Input type="date" className="mt-1.5 bg-white" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})}/>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Deadline</Label>
              <Input type="date" className="mt-1.5 bg-white" value={form.deadline} onChange={(e) => setForm({...form, deadline: e.target.value})} data-testid="prod-form-deadline"/>
            </div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Catatan</Label>
            <Input className="mt-1.5 bg-white" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#8B262D] hover:bg-[#6E1C22] text-white" data-testid="prod-form-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Plus className="w-4 h-4 mr-1.5"/>} Buat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductionUpdate({ job, isManager, onClose }) {
  const [form, setForm] = useState({ stage: job.stage, progress_pct: job.progress_pct||0, notes: job.notes||"", deadline: job.deadline||"", client_visible: job.client_visible !== false });
  const [busy, setBusy] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadEvidence = async () => {
    try {
      const { data } = await api.get(`/production/${job.id}/evidence`);
      setEvidence(data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadEvidence(); }, [job.id]);

  const uploadEvidence = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", "");
    fd.append("client_visible", "true");
    setUploading(true);
    try {
      await api.post(`/production/${job.id}/evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Bukti diunggah");
      loadEvidence();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload gagal"); }
    finally { setUploading(false); }
  };
  const removeEvidence = async (eid) => {
    if (!confirm("Hapus bukti ini?")) return;
    try { await api.delete(`/production/${job.id}/evidence/${eid}`); loadEvidence(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleClientVisible = async (ev) => {
    try {
      await api.patch(`/production/${job.id}/evidence/${ev.id}`, { client_visible: !ev.client_visible });
      loadEvidence();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const patch = isManager ? form : { stage: form.stage, progress_pct: Number(form.progress_pct), notes: form.notes };
      await api.patch(`/production/${job.id}`, patch);
      toast.success("Progress diperbarui");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!isManager || !confirm("Hapus production job ini?")) return;
    try { await api.delete(`/production/${job.id}`); toast.success("Job dihapus"); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="production-update-modal">
        <DialogHeader>
          <DialogTitle>Update: {job.booking_code} — {job.client_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-2 rounded bg-[#FAF6EE] text-xs text-[#5C6066]">{job.event_name} • {job.job_type}</div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Stage</Label>
            <Select value={form.stage} onValueChange={(v) => setForm({...form, stage: v})}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="prod-update-stage"><SelectValue/></SelectTrigger>
              <SelectContent>{PRODUCTION_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Progress %</Label>
            <Input type="number" min="0" max="100" className="mt-1.5 bg-white" value={form.progress_pct}
              onChange={(e) => setForm({...form, progress_pct: e.target.value})} data-testid="prod-update-progress"/>
          </div>
          {isManager && (
            <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Deadline</Label>
              <Input type="date" className="mt-1.5 bg-white" value={form.deadline||""} onChange={(e) => setForm({...form, deadline: e.target.value})}/>
            </div>
          )}
          <div><Label className="text-xs uppercase tracking-widest text-[#8C9096]">Catatan</Label>
            <Input className="mt-1.5 bg-white" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="prod-update-notes"/>
          </div>
          {isManager && (
            <label className="flex items-center gap-2 text-xs text-[#5C6066]">
              <input type="checkbox" checked={form.client_visible} onChange={(e) => setForm({...form, client_visible: e.target.checked})}/>
              Tampilkan progress ke Portal Klien (versi ringkas)
            </label>
          )}

          {/* Evidence uploader */}
          <div className="pt-3 border-t border-[#E5E0D8]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-[#8C9096]">Bukti Produksi ({evidence.length})</Label>
              <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#8B262D] text-[#8B262D] text-xs cursor-pointer hover:bg-[#8B262D] hover:text-white transition-colors" data-testid="prod-evidence-upload-label">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>}
                Upload
                <input type="file" accept="image/*,video/*" className="hidden"
                  onChange={(e) => { uploadEvidence(e.target.files[0]); e.target.value=""; }} data-testid="prod-evidence-input"/>
              </label>
            </div>
            {evidence.length === 0 ? (
              <div className="text-[11px] text-[#8C9096] italic">Belum ada bukti diunggah</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {evidence.map(ev => (
                  <div key={ev.id} className="relative rounded overflow-hidden border border-[#E5E0D8] group" data-testid={`prod-evidence-${ev.id}`}>
                    {ev.kind === "video" ? (
                      <div className="w-full aspect-square bg-black flex items-center justify-center">
                        <Video className="w-8 h-8 text-white"/>
                      </div>
                    ) : (
                      <img src={ev.url} alt={ev.caption||""} className="w-full aspect-square object-cover"/>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] px-1 py-0.5 uppercase tracking-wider truncate">{ev.stage}</div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                      {isManager && (
                        <button onClick={() => toggleClientVisible(ev)} className="p-1.5 bg-white/90 rounded" title={ev.client_visible ? "Sembunyikan dari klien" : "Tampilkan ke klien"}>
                          {ev.client_visible ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5 text-rose-600"/>}
                        </button>
                      )}
                      <button onClick={() => removeEvidence(ev.id)} className="p-1.5 bg-white/90 rounded" title="Hapus">
                        <Trash2 className="w-3.5 h-3.5 text-rose-600"/>
                      </button>
                    </div>
                    {!ev.client_visible && (
                      <div className="absolute top-1 right-1 bg-zinc-800 text-white text-[9px] px-1 rounded uppercase">Internal</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="justify-between">
          {isManager && <Button variant="ghost" onClick={del} className="text-rose-700 hover:bg-rose-50">Hapus</Button>}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={onClose}>Batal</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#8B262D] hover:bg-[#6E1C22] text-white" data-testid="prod-update-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : null} Simpan
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
